<template>
  <div>
    <section-header class="w-full mt-2" title="Buttons" @remove-section="removeSection"/>
    <div class="text-gray-dark">
      <vs-collapse accordion type="margin" class="px-0" :key="collapseKey">
        <draggable-container :items="buttons"
                             :is-disabled="openButtonIndex !== -1"
                             @change-order="reorderButtons"
                             @is-dragging="draggingStatus => isDragging = draggingStatus">
          <vs-collapse-item v-for="(button, i) in buttons"
                            :ref="`button${i}`"
                            not-arrow
                            :open="isOpen(i)"
                            :key="i + collapseKey"
                            class="shadow-none border-solid border border-soft rounded editable-button p-0 cursor-default"
                            :class="{'cursor-grabbing': isDragging}">

            <div slot="header"
                 @click="() => toggleButtonOpened(i)"
                 class="text-center text-sm my-4 mx-5 py-3 editable-button-header select-none border border-solid"
                 :class="resolveHeaderClasses(i)"
                 @mouseover="isHover = true"
                 @mouseleave="isHover = false">
              <span class="text-gray-dark cursor-pointer">
                {{ substituteText(button.title) }}
              </span>
              <vs-icon v-if="openButtonIndex === -1 && isHover"
                       icon="drag_indicator" size="1.5rem"
                       class="absolute left-0 ml-6 text-gray-dark"/>
            </div>

            <button-editor v-if="openButtonIndex === i"
                           :ref="`buttonEditor${i}`"
                           :button="button"
                           :index="i"
                           :bot-id="botId"
                           @remove-button="() => removeButton(i)"
                           @update-button="button => updateButton(i, button)"/>
          </vs-collapse-item>
        </draggable-container>

        <div v-if="buttons.length < 3"
             class="flex border-dashed border-soft rounded cursor-pointer test-add-button"
             @click="addButton"
        >
          <vs-icon class="ml-auto my-auto" icon="add"/>
          <span class="my-6 mr-auto text-sm">Add Button</span>
        </div>
      </vs-collapse>
    </div>
  </div>
</template>

<script>
import { BUTTON_TYPES, SECTION_FOCUSES } from '@/store/constants'
import contentUtils from '@/utils/content.utils'
import clone from 'ramda/src/clone'
import remove from 'ramda/src/remove'
import assocPath from 'ramda/src/assocPath'
import SectionHeader from './SectionHeader'
import tailwind from '../../../../tailwind.config'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'
import ButtonEditor from './ButtonEditor'
import templateModule from '@/store/modules/template.module'
import answerController from '@/controllers/answer.controller'
import templateController from '@/controllers/template.controller'
import outputChatBubbleModule from '@/store/modules/output.chat-bubble.module'
import DraggableContainer from '../DraggableContainer.vue'
import variableRefsUtils from '@/utils/variable-refs.utils'

export default {
  components: {
    ButtonEditor,
    SectionHeader,
    DraggableContainer
  },

  props: {
    botId: {
      type: String,
      required: true
    }
  },

  watch: {
    actionId() {
      this.closeButtonCollapses()
    },

    focus: {
      immediate: true,
      handler(focus) {
        // PROBLEM: when TextSection is focused and user clicks on button
        // TextSection.handleBlur() sets sectionFocus to NONE, so the watch logic would reset this component before handling the click event
        // HACK: setTimeout to delay the logic until the click event is handled
        setTimeout(() => {
          if (focus?.startsWith(SECTION_FOCUSES.BUTTON)) {
            const index = chatBubbleModule.getIndexFromFocus(focus, SECTION_FOCUSES.BUTTON)
            if (this.isOpen(index)) {
              chatBubbleModule.focusSection(SECTION_FOCUSES.NONE)
              return
            }

            this.openButton(index)
            return
          }

          this.closeButtonCollapses()
        }, 30)
      }
    }

  },

  data() {
    return {
      BUTTON_TYPES,
      SECTION_FOCUSES,
      isDragging: false,
      isHover: false,
      collapseKey: 0,
      openButtonIndex: -1
    }
  },

  computed: {
    actionId: chatBubbleModule.actionId,
    focus: chatBubbleModule.sectionFocus,
    stateTree: chatBubbleModule.stateTree,

    stateId() {
      return chatBubbleModule.bubbleSelected.get().stateId
    },

    action() {
      return conversationModule.getAction(this.stateId, this.actionId)
    },

    text() {
      return contentUtils.getText(this.action)
    },

    buttons() {
      return contentUtils.getButtons(this.action)
    },

    previewController() {
      return templateModule.isTemplateEditor() ? templateController : answerController
    },

    quickReplies() {
      return contentUtils.getQuickReplies(this.action)
    },

    focusColor() {
      return tailwind.theme.colors.focus
    }
  },

  methods: {
    resolveHeaderClasses(i) {
      if (this.isOpen(i)) return ['border-focus', 'cursor-pointer']

      const classes = ['border-transparent']
      if (this.isDragging && this.openButtonIndex === -1) {
        classes.push('cursor-grabbing')
      } else if (this.openButtonIndex === -1) {
        classes.push('cursor-grab')
      } else {
        classes.push('cursor-pointer')
      }

      return classes
    },

    reorderButtons(fromIndex, toIndex) {
      answerController.reorderButtons(this.actionId, this.text, this.buttons, this.quickReplies, fromIndex, toIndex)
    },

    isOpen(i) {
      return i === this.openButtonIndex
    },

    toggleButtonOpened(i) {
      // the content of vs-collapse-item is at the moment of item opening missing in DOM, so once related methods
      // are called, it causes calling xy method on undefined. Probably caused by draggable component between vs-collapse
      // and vs-collapse-item
      if (this.$refs[`button${i}`]?.[0]) {
        this.$refs[`button${i}`][0].$refs.content = this.$refs[`buttonEditor${i}`]
      }
      if (this.isOpen(i)) {
        chatBubbleModule.focusSection(SECTION_FOCUSES.NONE)
        return
      }

      chatBubbleModule.focusSection(SECTION_FOCUSES.BUTTON + i)
    },

    openButton(i) {
      this.openButtonIndex = i
      this.resetCollapseKey()
    },

    addButton() {
      const actionWithId = { id: this.actionId, ...this.action }
      outputChatBubbleModule.addActionButton(this.stateId, actionWithId, `Button ${this.buttons.length + 1}`)

      const lastIndex = this.buttons.length - 1
      this.toggleButtonOpened(lastIndex)

      this.resetCollapseKey()
    },

    updateButton(i, button) {
      const content = this.action.content
      const newContent = assocPath(['payload', 'buttons', i], button, content)
      this.addOutputAction(newContent)

      // HACK: Collapse must recalculate it's size when all components are displayed. Otherwise, it shows only a part of GUI.
      this.$nextTick(() => {
        const buttonKey = `button${i}`
        this.$refs[buttonKey][0].changeHeight()
      })
    },

    removeButton(i) {
      if (this.buttons[i].type === BUTTON_TYPES.POSTBACK) {
        this.previewController.removePostback(this.buttons[i].payload, this.stateTree, templateModule.getTemplateId())
      }

      const initRootJson = clone(conversationModule.getRootJson())
      chatBubbleModule.focusSection(SECTION_FOCUSES.NONE)
      const buttons = remove(i, 1, this.buttons)
      const content = contentUtils.createContent(this.text, buttons, this.quickReplies)
      this.addOutputAction(content)
      if (!templateModule.isTemplateEditor()) {
        templateModule.deleteFromTemplateRefsCache(initRootJson)
      }
      this.closeButtonCollapses()
    },

    removeSection() {
      chatBubbleModule.focusSection(SECTION_FOCUSES.NONE)
      while (this.buttons.length) {
        this.removeButton(0)
      }
    },

    addOutputAction(content) {
      conversationModule.addOutputAction(this.actionId, content)
    },

    closeButtonCollapses() {
      this.resetCollapseKey()
      this.openButtonIndex = -1
    },

    resetCollapseKey() {
      this.collapseKey = Math.random()
    },

    substituteText(text) {
      return variableRefsUtils.substituteVariableRefs(text)
    }
  }
}
</script>

<style scoped lang="scss">
.editable-button.open-item {
  // noinspection CssInvalidFunction - theme() belongs to TailWind
  box-shadow: 0 10px 30px theme('colors.gray-a10') !important;
}

.editable-button.open-item header {
  padding: 0.5rem !important;
}

.editable-button.open-item > header > .editable-button-header {
  border: 1px solid;
  // noinspection CssInvalidFunction - theme() belongs to TailWind
  border-color: theme('colors.focus') !important;
  border-radius: 4px;
}
</style>

<style lang="scss">
.editable-button {
  .vs-collapse-item--content {
    @apply overflow-visible;
  }
}
</style>
